import { useState } from 'react';

const inputClasses = 'w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-colors';
const labelClasses = 'block text-sm font-medium text-gray-300 mb-1';

export function EmailSubscribeForm({ source = 'unknown' }) {
  const [state, setState] = useState({ email: '', firstName: '', role: '', honeypot: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state.honeypot) return; // bot
    setStatus('loading');
    try {
      const res = await fetch('/.netlify/functions/submit-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'subscribe', ...state, source }),
      });
      if (res.ok) setStatus('success');
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') return (
    <div className="p-5 bg-green-500/10 border border-green-400/30 rounded-xl text-center">
      <p className="font-semibold text-green-400">You're on the list.</p>
      <p className="text-sm text-gray-400 mt-1">We'll send APD updates, compliance alerts, and resources directly to your inbox.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-6">
      <h3 className="text-lg font-bold text-white mb-1">Stay Current on APD</h3>
      <p className="text-sm text-gray-400 mb-4">
        Provider updates, iBudget changes, and compliance alerts — free, straight to your inbox.
      </p>

      {/* Honeypot — hidden from humans, bots fill it */}
      <input
        type="text"
        value={state.honeypot}
        onChange={e => setState(s => ({ ...s, honeypot: e.target.value }))}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="flex flex-col gap-3">
        <input
          placeholder="First name (optional)"
          value={state.firstName}
          onChange={e => setState(s => ({ ...s, firstName: e.target.value }))}
          className={inputClasses}
        />
        <input
          placeholder="Your email *"
          type="email"
          required
          value={state.email}
          onChange={e => setState(s => ({ ...s, email: e.target.value }))}
          className={inputClasses}
        />
        <select
          value={state.role}
          onChange={e => setState(s => ({ ...s, role: e.target.value }))}
          className={`${inputClasses} ${!state.role ? 'text-gray-500' : ''}`}
        >
          <option value="">I am a… (optional)</option>
          <option value="provider">APD Provider / Group Home Operator</option>
          <option value="wsc">Waiver Support Coordinator (WSC)</option>
          <option value="family">Family Member / Guardian</option>
          <option value="consultant">Consultant / Licensing Professional</option>
          <option value="other">Other</option>
        </select>
        <button
          type="submit"
          disabled={!state.email || status === 'loading'}
          className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold text-sm hover:shadow-lg hover:shadow-violet-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? 'Subscribing...' : 'Subscribe — Free'}
        </button>
        {status === 'error' && <p className="text-red-400 text-sm">Something went wrong. Try again.</p>}
      </div>
    </form>
  );
}


export function ConsultingIntakeForm({ sourcePage = 'unknown' }) {
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', organization: '',
    serviceInterest: '', message: '', honeypot: '',
  });
  const [status, setStatus] = useState('idle');
  const set = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    setStatus('loading');
    try {
      const res = await fetch('/.netlify/functions/submit-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'consulting', ...form, sourcePage }),
      });
      if (res.ok) setStatus('success');
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') return (
    <div className="p-6 bg-green-500/10 border border-green-400/30 rounded-xl">
      <p className="font-semibold text-green-400">Message received.</p>
      <p className="text-sm text-gray-400 mt-1">A Pathway Licensing consultant will reach out within 1 business day.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-6">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-1">Get Licensing Help</h3>
      <p className="text-sm text-gray-400 mb-5">
        Pathway Licensing Consulting — Florida APD &amp; DCF provider licensing specialists.
      </p>

      <input type="text" value={form.honeypot} onChange={set('honeypot')} className="hidden" tabIndex={-1} aria-hidden="true" />

      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClasses}>Full Name *</label>
            <input placeholder="Jane Smith" value={form.fullName} onChange={set('fullName')} required className={inputClasses} />
          </div>
          <div>
            <label className={labelClasses}>Email *</label>
            <input placeholder="jane@example.com" type="email" value={form.email} onChange={set('email')} required className={inputClasses} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClasses}>Phone (optional)</label>
            <input placeholder="(555) 123-4567" value={form.phone} onChange={set('phone')} className={inputClasses} />
          </div>
          <div>
            <label className={labelClasses}>Organization</label>
            <input placeholder="Business name" value={form.organization} onChange={set('organization')} className={inputClasses} />
          </div>
        </div>
        <div>
          <label className={labelClasses}>Service Interest</label>
          <select value={form.serviceInterest} onChange={set('serviceInterest')} className={`${inputClasses} ${!form.serviceInterest ? 'text-gray-500' : ''}`}>
            <option value="">Select a service (optional)</option>
            <option value="apd_licensing">APD Group Home Licensing</option>
            <option value="dcf_licensing">DCF Facility Licensing</option>
            <option value="compliance">Compliance Audit / Remediation</option>
            <option value="wsc">Waiver Support Coordination Setup</option>
            <option value="other">Other / Not Sure</option>
          </select>
        </div>
        <div>
          <label className={labelClasses}>Tell us about your situation</label>
          <textarea
            placeholder="What can we help with?"
            value={form.message}
            onChange={set('message')}
            rows={4}
            className={`${inputClasses} resize-y`}
          />
        </div>
        <button
          type="submit"
          disabled={!form.fullName || !form.email || status === 'loading'}
          className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-slate-700 to-slate-800 border border-white/10 text-white font-semibold text-sm hover:from-slate-600 hover:to-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? 'Submitting...' : 'Request Free Consultation'}
        </button>
        {status === 'error' && <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>}
      </div>
    </form>
  );
}
